/**
 * Notification Settings Page Component
 *
 * Allows users to manage their notification preferences
 */

import { buildAppNav, APP_NAV_STYLES } from '../lib/app-nav.js';
import { BaseComponent } from '../lib/BaseComponent';
import { api } from '../lib/api';
import { router } from '../lib/router';
import { Toast } from './shared/Toast';

interface NotificationPreferences {
  daily_digest: boolean;
  weekly_summary: boolean;
  monthly_report: boolean;
  inspection_overdue: boolean;
  user_activity: boolean;
  sms_enabled: boolean;
  email_enabled: boolean;
  quiet_hours_start: string;
  quiet_hours_end: string;
  timezone: string;
}

export class NotificationSettingsPage extends BaseComponent {
  private preferences: NotificationPreferences | null = null;
  private loading: boolean = true;
  private saving: boolean = false;

  async connectedCallback() {
    super.connectedCallback();
    await this.loadPreferences();
    // Replace the loading state instead of stacking a second copy of the page.
    this.shadow.replaceChildren();
    await this.render();
  }

  private async loadPreferences(): Promise<void> {
    try {
      const response = await api.get('/api/notification-preferences');
      this.preferences = response.preferences;
      this.loading = false;
    } catch (error) {
      console.error('Failed to load notification preferences:', error);
      this.loading = false;
      Toast.error('Failed to load notification preferences');
    }
  }

  protected async render(): Promise<void> {
    this.addStyles(this.getCommonStyles());
    this.addStyles(this.getStyles());

    const container = this.createElement('div', { className: 'settings-page' });

    // Header
    const header = this.createHeader();
    container.appendChild(header);

    // Main content
    const main = this.createElement('main', { className: 'settings-main' });

    if (this.loading) {
      main.appendChild(this.createElement('loading-spinner'));
    } else {
      // Page title
      const pageTitle = this.createElement('div', { className: 'page-title' });
      pageTitle.appendChild(this.createElement('h1', { textContent: '🔔 Notification Settings' }));
      pageTitle.appendChild(this.createElement('p', {
        className: 'subtitle',
        textContent: 'Manage your email notification preferences and schedules'
      }));
      main.appendChild(pageTitle);

      // Settings form
      const form = this.createSettingsForm();
      main.appendChild(form);
    }

    container.appendChild(main);
    this.shadow.appendChild(container);
  }

  private createHeader(): HTMLElement {
    const header = this.createElement('header', { className: 'page-header' });
    const headerContent = this.createElement('div', { className: 'header-content' });

    const logo = this.createElement('h1', {
      className: 'logo',
      textContent: 'FireGuard'
    });
    logo.style.cursor = 'pointer';
    logo.addEventListener('click', () => router.push('/dashboard'));

    const nav = buildAppNav((t, p, txt) => this.createElement(t as any, p as any, txt as any), '/settings', () => this.handleLogout());
    headerContent.appendChild(nav);
    header.appendChild(headerContent);

    return header;
  }

  private createSettingsForm(): HTMLElement {
    const formContainer = this.createElement('div', { className: 'settings-container' });

    // Email notifications section
    const emailSection = this.createSection(
      '📧 Email Notifications',
      'Control which email notifications you receive'
    );

    const emailToggle = this.createToggle(
      'Email notifications',
      'email_enabled',
      this.preferences?.email_enabled || false,
      'Receive notifications via email'
    );
    emailSection.appendChild(emailToggle);

    formContainer.appendChild(emailSection);

    // Notification types section
    const typesSection = this.createSection(
      '📨 Notification Types',
      'Choose which types of notifications to receive'
    );

    const dailyToggle = this.createToggle(
      'Daily Digest',
      'daily_digest',
      this.preferences?.daily_digest || false,
      'Receive a daily summary of overdue and upcoming inspections'
    );
    typesSection.appendChild(dailyToggle);

    const weeklyToggle = this.createToggle(
      'Weekly Summary',
      'weekly_summary',
      this.preferences?.weekly_summary || false,
      'Receive a weekly compliance summary every Monday'
    );
    typesSection.appendChild(weeklyToggle);

    const monthlyToggle = this.createToggle(
      'Monthly Report',
      'monthly_report',
      this.preferences?.monthly_report || false,
      'Receive a comprehensive monthly report on the 1st of each month'
    );
    typesSection.appendChild(monthlyToggle);

    const overdueToggle = this.createToggle(
      'Overdue Alerts',
      'inspection_overdue',
      this.preferences?.inspection_overdue || false,
      'Receive immediate alerts when inspections become overdue'
    );
    typesSection.appendChild(overdueToggle);

    const activityToggle = this.createToggle(
      'User Activity',
      'user_activity',
      this.preferences?.user_activity || false,
      'Receive notifications about team member activity (inspections, changes)'
    );
    typesSection.appendChild(activityToggle);

    formContainer.appendChild(typesSection);

    // SMS notifications section (future feature)
    const smsSection = this.createSection(
      '📱 SMS Notifications',
      'Receive text message notifications (coming soon)'
    );

    const smsToggle = this.createToggle(
      'SMS notifications',
      'sms_enabled',
      this.preferences?.sms_enabled || false,
      'Receive critical alerts via SMS (feature coming soon)',
      true // disabled
    );
    smsSection.appendChild(smsToggle);

    formContainer.appendChild(smsSection);

    // Quiet hours section
    const quietSection = this.createSection(
      '🌙 Quiet Hours',
      'Set hours during which you will not receive non-urgent notifications'
    );

    const quietHoursForm = this.createElement('div', { className: 'quiet-hours-form' });

    const startLabel = this.createElement('label', { textContent: 'Start time:' });
    const startInput = this.createElement('input', {
      type: 'time',
      className: 'time-input',
      value: this.preferences?.quiet_hours_start || '22:00'
    }) as HTMLInputElement;
    startInput.id = 'quiet_hours_start';

    const endLabel = this.createElement('label', { textContent: 'End time:' });
    const endInput = this.createElement('input', {
      type: 'time',
      className: 'time-input',
      value: this.preferences?.quiet_hours_end || '08:00'
    }) as HTMLInputElement;
    endInput.id = 'quiet_hours_end';

    quietHoursForm.appendChild(startLabel);
    quietHoursForm.appendChild(startInput);
    quietHoursForm.appendChild(endLabel);
    quietHoursForm.appendChild(endInput);

    quietSection.appendChild(quietHoursForm);
    formContainer.appendChild(quietSection);

    // Action buttons
    const actions = this.createElement('div', { className: 'form-actions' });

    const saveBtn = this.createElement('button', {
      className: 'btn-primary',
      textContent: this.saving ? 'Saving...' : 'Save Changes'
    }) as HTMLButtonElement;
    saveBtn.disabled = this.saving;
    saveBtn.addEventListener('click', () => this.handleSave());

    const resetBtn = this.createElement('button', {
      className: 'btn-secondary',
      textContent: 'Reset to Defaults'
    });
    resetBtn.addEventListener('click', () => this.handleReset());

    actions.appendChild(saveBtn);
    actions.appendChild(resetBtn);
    formContainer.appendChild(actions);

    return formContainer;
  }

  private createSection(title: string, description: string): HTMLElement {
    const section = this.createElement('section', { className: 'settings-section' });

    const header = this.createElement('div', { className: 'section-header' });
    header.appendChild(this.createElement('h2', { textContent: title }));
    header.appendChild(this.createElement('p', {
      className: 'section-description',
      textContent: description
    }));

    section.appendChild(header);
    return section;
  }

  private createToggle(
    label: string,
    name: string,
    checked: boolean,
    description?: string,
    disabled: boolean = false
  ): HTMLElement {
    const toggleContainer = this.createElement('div', { className: 'toggle-container' });

    const toggle = this.createElement('label', { className: 'toggle' });

    const input = this.createElement('input', {
      type: 'checkbox',
      className: 'toggle-input'
    }) as HTMLInputElement;
    input.checked = checked;
    input.disabled = disabled;
    input.id = name;

    const slider = this.createElement('span', { className: 'toggle-slider' });

    toggle.appendChild(input);
    toggle.appendChild(slider);

    const labelContent = this.createElement('div', { className: 'toggle-label' });
    labelContent.appendChild(this.createElement('strong', { textContent: label }));

    if (description) {
      labelContent.appendChild(this.createElement('p', {
        className: 'toggle-description',
        textContent: description
      }));
    }

    toggleContainer.appendChild(toggle);
    toggleContainer.appendChild(labelContent);

    return toggleContainer;
  }

  private async handleSave(): Promise<void> {
    if (this.saving) return;

    this.saving = true;

    try {
      // Collect all form values
      const updates: Partial<NotificationPreferences> = {
        email_enabled: (this.shadow.getElementById('email_enabled') as HTMLInputElement)?.checked,
        daily_digest: (this.shadow.getElementById('daily_digest') as HTMLInputElement)?.checked,
        weekly_summary: (this.shadow.getElementById('weekly_summary') as HTMLInputElement)?.checked,
        monthly_report: (this.shadow.getElementById('monthly_report') as HTMLInputElement)?.checked,
        inspection_overdue: (this.shadow.getElementById('inspection_overdue') as HTMLInputElement)?.checked,
        user_activity: (this.shadow.getElementById('user_activity') as HTMLInputElement)?.checked,
        sms_enabled: (this.shadow.getElementById('sms_enabled') as HTMLInputElement)?.checked,
        quiet_hours_start: (this.shadow.getElementById('quiet_hours_start') as HTMLInputElement)?.value,
        quiet_hours_end: (this.shadow.getElementById('quiet_hours_end') as HTMLInputElement)?.value,
      };

      await api.put('/api/notification-preferences', updates);

      Toast.success('Notification preferences saved successfully');
      this.preferences = { ...this.preferences, ...updates } as NotificationPreferences;
    } catch (error: any) {
      console.error('Failed to save notification preferences:', error);
      Toast.error(error.message || 'Failed to save notification preferences');
    } finally {
      this.saving = false;
    }
  }

  private async handleReset(): Promise<void> {
    if (!confirm('Are you sure you want to reset all notification preferences to defaults?')) {
      return;
    }

    try {
      const response = await api.post('/api/notification-preferences/reset', {});
      this.preferences = response.preferences;

      Toast.success('Notification preferences reset to defaults');

      // Re-render to show updated values
      this.shadow.innerHTML = '';
      await this.render();
    } catch (error: any) {
      console.error('Failed to reset notification preferences:', error);
      Toast.error(error.message || 'Failed to reset notification preferences');
    }
  }

  private handleLogout(): void {
    api.logout();
    router.push('/login');
  }

  private getStyles(): string {
    return `
      ${APP_NAV_STYLES}

      .settings-page {
        min-height: 100vh;
        background: var(--bg-primary);
      }

      .page-header {
        background: white;
        border-bottom: 1px solid var(--border-color);
        padding: 16px 24px;
        box-shadow: var(--shadow-sm);
      }

      .header-content {
        max-width: 1200px;
        margin: 0 auto;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .logo {
        font-size: 24px;
        font-weight: bold;
        color: var(--primary-color);
        margin: 0;
      }

      .nav {
        display: flex;
        gap: 12px;
        align-items: center;
      }

      .nav-button {
        background: none;
        border: none;
        color: var(--text-primary);
        font-size: 15px;
        cursor: pointer;
        padding: 8px 16px;
        border-radius: 6px;
        transition: all 0.2s;
      }

      .nav-button:hover {
        background: var(--bg-secondary);
      }

      .settings-main {
        max-width: 800px;
        margin: 0 auto;
        padding: 32px 24px;
      }

      .page-title {
        margin-bottom: 32px;
      }

      .page-title h1 {
        font-size: 32px;
        color: var(--text-primary);
        margin: 0 0 8px 0;
      }

      .subtitle {
        color: var(--text-secondary);
        font-size: 16px;
        margin: 0;
      }

      .settings-container {
        background: white;
        border-radius: 12px;
        padding: 32px;
        box-shadow: var(--shadow-sm);
      }

      .settings-section {
        margin-bottom: 40px;
      }

      .settings-section:last-of-type {
        margin-bottom: 32px;
      }

      .section-header h2 {
        font-size: 20px;
        color: var(--text-primary);
        margin: 0 0 8px 0;
      }

      .section-description {
        color: var(--text-secondary);
        font-size: 14px;
        margin: 0 0 24px 0;
      }

      .toggle-container {
        display: flex;
        align-items: flex-start;
        gap: 16px;
        padding: 16px;
        background: var(--bg-secondary);
        border-radius: 8px;
        margin-bottom: 12px;
      }

      .toggle {
        position: relative;
        display: inline-block;
        width: 48px;
        height: 24px;
        flex-shrink: 0;
        margin-top: 4px;
      }

      .toggle-input {
        opacity: 0;
        width: 0;
        height: 0;
      }

      .toggle-slider {
        position: absolute;
        cursor: pointer;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: #ccc;
        transition: 0.4s;
        border-radius: 24px;
      }

      .toggle-slider:before {
        position: absolute;
        content: "";
        height: 18px;
        width: 18px;
        left: 3px;
        bottom: 3px;
        background-color: white;
        transition: 0.4s;
        border-radius: 50%;
      }

      .toggle-input:checked + .toggle-slider {
        background-color: var(--primary-color);
      }

      .toggle-input:checked + .toggle-slider:before {
        transform: translateX(24px);
      }

      .toggle-input:disabled + .toggle-slider {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .toggle-label {
        flex: 1;
      }

      .toggle-label strong {
        display: block;
        color: var(--text-primary);
        margin-bottom: 4px;
      }

      .toggle-description {
        color: var(--text-secondary);
        font-size: 14px;
        margin: 0;
        line-height: 1.4;
      }

      .quiet-hours-form {
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 16px;
        align-items: center;
        padding: 16px;
        background: var(--bg-secondary);
        border-radius: 8px;
      }

      .quiet-hours-form label {
        font-weight: 500;
        color: var(--text-primary);
      }

      .time-input {
        padding: 10px;
        border: 1px solid var(--border-color);
        border-radius: 6px;
        font-size: 14px;
        background: white;
      }

      .time-input:focus {
        outline: none;
        border-color: var(--primary-color);
      }

      .form-actions {
        display: flex;
        gap: 12px;
        justify-content: flex-end;
        padding-top: 24px;
        border-top: 1px solid var(--border-color);
      }

      .btn-primary,
      .btn-secondary {
        padding: 12px 24px;
        font-size: 15px;
        font-weight: 600;
        border-radius: 6px;
        border: none;
        cursor: pointer;
        transition: all 0.2s;
      }

      .btn-primary {
        background: var(--primary-color);
        color: white;
      }

      .btn-primary:hover:not(:disabled) {
        background: var(--primary-hover);
        transform: translateY(-1px);
        box-shadow: var(--shadow-md);
      }

      .btn-primary:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      .btn-secondary {
        background: var(--text-primary);
        color: white;
      }

      .btn-secondary:hover {
        background: #374151;
      }

      @media (max-width: 768px) {
        .settings-main {
          padding: 16px;
        }

        .settings-container {
          padding: 24px 16px;
        }

        .toggle-container {
          flex-direction: column;
          gap: 12px;
        }

        .quiet-hours-form {
          grid-template-columns: 1fr;
        }

        .form-actions {
          flex-direction: column;
        }

        .btn-primary,
        .btn-secondary {
          width: 100%;
        }
      }
    `;
  }
}

customElements.define('notification-settings-page', NotificationSettingsPage);
